/**
 * Transform backend chart_config and data into Nivo-compatible formats
 */

/**
 * Transform data for Nivo Line/Area charts
 * @param {Object} chartConfig - Backend chart configuration
 * @param {Array} data - Raw data from backend
 * @returns {Array} Nivo-formatted data
 */
export const transformToNivoLine = (chartConfig, data) => {
  if (!chartConfig || !data || data.length === 0) {
    return [];
  }

  const { x_axis, y_axis, long_format_series, data_format } = chartConfig;
  
  if (!x_axis || !y_axis || !y_axis.columns || y_axis.columns.length === 0) {
    return [];
  }

  const xColumn = x_axis.column;
  const yColumn = y_axis.columns[0]; // Use first y-axis column
  
  // Long format: Multiple series, one value column
  if (data_format === 'long' && long_format_series && long_format_series.column) {
    const seriesColumn = long_format_series.column;
    
    // Get unique series values
    const uniqueSeries = [...new Set(data.map(row => row[seriesColumn]).filter(Boolean))];
    
    // Transform to Nivo format
    return uniqueSeries.map(seriesValue => ({
      id: seriesValue,
      data: data
        .filter(row => row[seriesColumn] === seriesValue)
        .map(row => ({
          x: row[xColumn],
          y: row[yColumn] !== null && row[yColumn] !== undefined ? row[yColumn] : null
        }))
        .sort((a, b) => a.x - b.x) // Sort by x value
    }));
  }
  
  // Wide format: Multiple value columns, one row per x-value
  if (data_format === 'wide' || !long_format_series) {
    // Each y-axis column becomes a series
    return y_axis.columns.map(column => {
      const seriesData = data
        .map(row => ({
          x: row[xColumn],
          y: row[column] !== null && row[column] !== undefined ? row[column] : null
        }))
        .filter(point => point.x !== null && point.x !== undefined); // Filter out null x values
      
      // Only sort if x values are numeric
      const firstX = seriesData[0]?.x;
      if (typeof firstX === 'number') {
        seriesData.sort((a, b) => a.x - b.x);
      }
      
      return {
        id: column,
        data: seriesData
      };
    });
  }
  
  // Fallback: treat as wide format
  return y_axis.columns.map(column => {
    const seriesData = data
      .map(row => ({
        x: row[xColumn],
        y: row[column] !== null && row[column] !== undefined ? row[column] : null
      }))
      .filter(point => point.x !== null && point.x !== undefined);
    
    return {
      id: column,
      data: seriesData
    };
  });
};

/**
 * Transform data for Nivo Bar chart
 * @param {Object} chartConfig - Backend chart configuration
 * @param {Array} data - Raw data from backend
 * @returns {Array} Nivo-formatted data
 */
export const transformToNivoBar = (chartConfig, data) => {
  if (!chartConfig || !data || data.length === 0) {
    return [];
  }

  const { x_axis, y_axis, long_format_series, data_format } = chartConfig;
  
  if (!x_axis || !y_axis || !y_axis.columns || y_axis.columns.length === 0) {
    return [];
  }

  const xColumn = x_axis.column;
  
  // Long format: pivot to wide format for Nivo bar chart
  if (data_format === 'long' && long_format_series && long_format_series.column) {
    const seriesColumn = long_format_series.column;
    const yColumn = y_axis.columns[0];
    
    // Get unique x and series values
    const uniqueXValues = [...new Set(data.map(row => row[xColumn]))].sort((a, b) => a - b);
    const uniqueSeries = [...new Set(data.map(row => row[seriesColumn]).filter(Boolean))];
    
    // Pivot data: one row per x-value, one column per series
    return uniqueXValues.map(xValue => {
      const row = { [xColumn]: xValue };
      
      uniqueSeries.forEach(seriesValue => {
        const dataPoint = data.find(
          d => d[xColumn] === xValue && d[seriesColumn] === seriesValue
        );
        row[seriesValue] = dataPoint ? dataPoint[yColumn] : null;
      });
      
      return row;
    });
  }
  
  // Categorical with long_format_series: pivot each row so Nivo can color by series
  if (data_format === 'categorical' && long_format_series && long_format_series.column) {
    const seriesColumn = long_format_series.column;
    const yColumn = y_axis.columns[0];
    const uniqueXValues = [...new Set(data.map(row => row[xColumn]))];
    const uniqueSeries = [...new Set(data.map(row => row[seriesColumn]).filter(Boolean))];

    return uniqueXValues.map(xValue => {
      const pivotRow = { [xColumn]: xValue };
      uniqueSeries.forEach(seriesValue => {
        const match = data.find(d => d[xColumn] === xValue && d[seriesColumn] === seriesValue);
        pivotRow[seriesValue] = match ? match[yColumn] : null;
      });
      return pivotRow;
    });
  }

  // Wide format: data is already in correct format for Nivo
  if (data_format === 'wide' || data_format === 'categorical') {
    return data;
  }
  
  // Fallback
  return data;
};

/**
 * Transform data for Nivo Pie chart
 * @param {Object} chartConfig - Backend chart configuration
 * @param {Array} data - Raw data from backend
 * @returns {Array} Nivo-formatted data
 */
export const transformToNivoPie = (chartConfig, data) => {
  if (!chartConfig || !data || data.length === 0) {
    return [];
  }

  const { x_axis, y_axis } = chartConfig;
  
  if (!x_axis || !y_axis || !y_axis.columns || y_axis.columns.length === 0) {
    return [];
  }

  const labelColumn = x_axis.column;
  const valueColumn = y_axis.columns[0];
  
  return data.map(row => ({
    id: row[labelColumn],
    label: row[labelColumn],
    value: row[valueColumn] || 0
  }));
};

/**
 * Get keys for Nivo bar chart (series names)
 * @param {Object} chartConfig - Backend chart configuration
 * @param {Array} data - Raw data from backend
 * @returns {Array} Array of series keys
 */
export const getNivoBarKeys = (chartConfig, data) => {
  if (!chartConfig || !data || data.length === 0) {
    return [];
  }

  const { y_axis, long_format_series, data_format } = chartConfig;
  
  // Long format or categorical with series: series values become bar keys
  if (long_format_series && long_format_series.column &&
      (data_format === 'long' || data_format === 'categorical')) {
    return [...new Set(data.map(row => row[long_format_series.column]).filter(Boolean))];
  }
  
  // Wide format: series are the y-axis columns
  return y_axis.columns || [];
};

/**
 * Get index by key for Nivo bar chart
 * @param {Object} chartConfig - Backend chart configuration
 * @returns {string} The column to use as index
 */
export const getNivoBarIndexBy = (chartConfig) => {
  return chartConfig?.x_axis?.column || 'x';
};
